// oxlint-disable react/only-export-components
import { cva } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { cn } from "cn"

import { Badge } from "../badge/badge"

/* -------------------------------------------------------------------------- */
/*  Data Display Prominent                                                    */
/* -------------------------------------------------------------------------- */

export type DataDisplayProminentSize = "lg" | "md" | "sm"
export type DataDisplayProminentVariant = "data" | "badge"
export type DataDisplayProminentState = "default" | "disabled"

const dataDisplayProminentVariants = cva("flex", {
  variants: {
    size: {
      lg: "items-center gap-4",
      md: "items-center gap-3",
      sm: "items-center gap-2",
    },
    variant: {
      data: "",
      badge: "flex-col items-start justify-center gap-0.5",
    },
  },
  defaultVariants: {
    size: "lg",
    variant: "data",
  },
})

const iconContainerVariants = cva(
  "flex shrink-0 items-center justify-center bg-neutrals-v2-200 [&>svg]:size-[var(--icon-size)]",
  {
    variants: {
      size: {
        lg: "size-12 rounded-xl [--icon-size:28px]",
        md: "size-11 rounded-lg [--icon-size:22px]",
        sm: "size-9 rounded-lg [--icon-size:18px]",
      },
    },
  }
)

const valueVariants = cva("font-[650]", {
  variants: {
    size: {
      lg: "text-2xl leading-8",
      md: "text-xl leading-7",
      sm: "text-sm leading-5 font-semibold",
    },
    state: {
      default: "text-labels-primary",
      disabled: "text-labels-secondary",
    },
  },
})

// Figma bildet die Einheit im Zustand „disabled“ je Größe leicht abweichend ab
// (md: 16/24 bold statt 14/20 semibold, lg: semibold statt bold, sm: 4px statt
// 2px Abstand). Normalisiert: Disabled ändert nur die Farben, nicht die Metrik.
const unitVariants = cva("", {
  variants: {
    size: {
      lg: "text-base leading-6 font-bold",
      md: "text-sm leading-5 font-semibold",
      sm: "text-sm leading-5 font-semibold",
    },
    state: {
      default: "text-labels-secondary",
      disabled: "text-labels-tertiary",
    },
  },
})

const dotClassName = {
  lg: "size-3",
  md: "size-2.5",
  sm: "size-2.5",
} as const

function DataDisplayNumber({
  value,
  unit,
  size,
  state,
}: {
  value?: ReactNode
  unit?: ReactNode
  size: DataDisplayProminentSize
  state: DataDisplayProminentState
}) {
  return (
    <div
      data-slot="data-display-number"
      className="flex items-baseline gap-0.5"
    >
      <span className={valueVariants({ size, state })}>{value}</span>
      {unit && <span className={unitVariants({ size, state })}>{unit}</span>}
    </div>
  )
}

export type DataDisplayProminentProps = Omit<ComponentProps<"div">, "title"> & {
  size?: DataDisplayProminentSize
  /**
   * `data`: Label + Zahl(en) mit Einheit. `badge`: Label + Badge mit `value1`
   * (Figma kennt die Badge-Variante nur in `size="sm"`).
   */
  variant?: DataDisplayProminentVariant
  state?: DataDisplayProminentState
  label?: ReactNode
  value1?: ReactNode
  unit1?: ReactNode
  showSecondValue?: boolean
  value2?: ReactNode
  unit2?: ReactNode
  /** Roter Farbpunkt vor dem Label. */
  showDot?: boolean
  showLeadingIcon?: boolean
  /** Icon im Icon-Container; wird nur mit `showLeadingIcon` und `variant="data"` gezeigt. */
  leadingIcon?: ReactNode
}

function DataDisplayProminent({
  className,
  size = "lg",
  variant = "data",
  state = "default",
  label,
  value1,
  unit1,
  showSecondValue = false,
  value2,
  unit2,
  showDot = false,
  showLeadingIcon = false,
  leadingIcon,
  ...props
}: DataDisplayProminentProps) {
  const isBadge = variant === "badge"
  const disabled = state === "disabled"

  const title = (
    <div data-slot="data-display-title" className="flex items-center gap-2">
      {showDot && (
        <span
          data-slot="data-display-dot"
          className={cn(
            "shrink-0 rounded-full bg-brand-red-200",
            dotClassName[isBadge ? "sm" : size]
          )}
        />
      )}
      <span
        className={cn(
          "text-xs leading-4 font-semibold whitespace-nowrap",
          disabled ? "text-labels-tertiary" : "text-labels-secondary"
        )}
      >
        {label}
      </span>
    </div>
  )

  return (
    <div
      data-slot="data-display-prominent"
      data-size={size}
      data-variant={variant}
      data-state={state}
      className={cn(dataDisplayProminentVariants({ size, variant }), className)}
      {...props}
    >
      {!isBadge && showLeadingIcon && leadingIcon && (
        <div
          data-slot="data-display-icon"
          className={iconContainerVariants({ size })}
        >
          {leadingIcon}
        </div>
      )}
      {isBadge ? (
        <>
          {title}
          <Badge
            layout="character-dot"
            size="sm"
            className={cn(disabled && "opacity-60")}
          >
            {value1}
          </Badge>
        </>
      ) : (
        <div className="flex flex-col items-start">
          {title}
          <div
            data-slot="data-display-data"
            className="flex items-center gap-1 whitespace-nowrap"
          >
            <DataDisplayNumber
              value={value1}
              unit={unit1}
              size={size}
              state={state}
            />
            {showSecondValue && (
              <DataDisplayNumber
                value={value2}
                unit={unit2}
                size={size}
                state={state}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Data Display Plain                                                        */
/* -------------------------------------------------------------------------- */

export type DataDisplayPlainVariant = "text" | "dot" | "badge" | "empty"
export type DataDisplayPlainOrientation = "horizontal" | "vertical"

const dataDisplayPlainVariants = cva("flex text-sm leading-5", {
  variants: {
    orientation: {
      horizontal: "items-start gap-1",
      vertical: "flex-col items-start gap-1",
    },
    variant: {
      text: "",
      dot: "",
      empty: "",
      badge: "",
    },
  },
  compoundVariants: [
    // Horizontal mit Badge: Label und Badge stehen mittig auf einer Linie.
    { orientation: "horizontal", variant: "badge", class: "items-center" },
    // Vertikal mit Badge: mehr Luft zwischen Label und Badge.
    { orientation: "vertical", variant: "badge", class: "gap-2" },
  ],
  defaultVariants: {
    orientation: "horizontal",
    variant: "text",
  },
})

export type DataDisplayPlainProps = Omit<ComponentProps<"div">, "title"> & {
  variant?: DataDisplayPlainVariant
  orientation?: DataDisplayPlainOrientation
  label?: ReactNode
  /** Wert; bei `variant="badge"` der Inhalt des Badges, bei `empty` ignoriert („--“). */
  item?: ReactNode
  showLabel?: boolean
  /** Nur bei `variant="text"` vor dem Wert; wird nur mit `showIcon` gezeigt. */
  icon?: ReactNode
  showIcon?: boolean
}

function DataDisplayPlain({
  className,
  variant = "text",
  orientation = "horizontal",
  label,
  item,
  showLabel = true,
  icon,
  showIcon = true,
  ...props
}: DataDisplayPlainProps) {
  const horizontal = orientation === "horizontal"
  // Im horizontalen Layout teilen sich Label und Wert die Breite (flex-1).
  const cell = horizontal ? "min-w-px flex-1" : "w-full"

  return (
    <div
      data-slot="data-display-plain"
      data-variant={variant}
      data-orientation={orientation}
      className={cn(
        dataDisplayPlainVariants({ orientation, variant }),
        "text-labels-primary",
        className
      )}
      {...props}
    >
      {showLabel && (
        <p
          data-slot="data-display-label"
          className={cn(
            "font-normal",
            variant === "badge" && horizontal ? "min-w-px flex-1" : cell
          )}
        >
          {label}
        </p>
      )}
      {variant === "text" && (
        <div
          data-slot="data-display-item"
          className={cn("flex items-center gap-2", cell)}
        >
          {showIcon && icon && (
            <span className="flex size-[18px] shrink-0 items-center justify-center [&>svg]:size-full">
              {icon}
            </span>
          )}
          <p className="min-w-px flex-1 font-semibold">{item}</p>
        </div>
      )}
      {variant === "dot" && (
        <div
          data-slot="data-display-item"
          className={cn("flex h-5 items-center gap-2", cell)}
        >
          <span
            data-slot="data-display-dot"
            className="size-2.5 shrink-0 rounded-full bg-brand-red-200"
          />
          <p className="min-w-px flex-1 font-semibold">{item}</p>
        </div>
      )}
      {variant === "empty" && (
        <p data-slot="data-display-item" className={cn("font-semibold", cell)}>
          --
        </p>
      )}
      {variant === "badge" && (
        <Badge layout="character-dot" size="md">
          {item}
        </Badge>
      )}
    </div>
  )
}

export {
  DataDisplayPlain,
  DataDisplayProminent,
  dataDisplayPlainVariants,
  dataDisplayProminentVariants,
}
